import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, MessageSquare, Bell, AlertCircle, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const messageTypes = [
  { value: 'announcement', label: 'Announcement', icon: MessageSquare, color: 'text-blue-400' },
  { value: 'reminder', label: 'Reminder', icon: Bell, color: 'text-amber-400' },
  { value: 'alert', label: 'Alert', icon: AlertCircle, color: 'text-red-400' },
];

const TeamMessages = () => {
  const { currentTeam, user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    message_type: 'announcement'
  });

  useEffect(() => {
    if (currentTeam) {
      fetchMessages();
    }
  }, [currentTeam]);

  // Check if should open dialog on load
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setDialogOpen(true);
      setSearchParams({});
    }
  }, [searchParams]);

  const fetchMessages = async () => {
    const token = localStorage.getItem('team_token');
    if (!token) return;

    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/messages?limit=50`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      title: '',
      message: '',
      message_type: 'announcement'
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('team_token');
    if (!token) return;

    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send message');
      }

      toast.success('Message sent to team!');
      handleCloseDialog();
      fetchMessages();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const getMessageTypeInfo = (type) => {
    return messageTypes.find(t => t.value === type) || messageTypes[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-messages">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading">Messages</h1>
          <p className="text-muted-foreground mt-1">
            Send announcements and updates to your team
          </p>
        </div>
        <Button onClick={handleOpenDialog} data-testid="new-message-btn">
          <Plus size={18} className="mr-2" />
          New Message
        </Button>
      </div>

      {/* Messages List */}
      {messages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Messages Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Send your first message to the team
            </p>
            <Button onClick={handleOpenDialog}>
              <Plus size={18} className="mr-2" />
              Send Message
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => {
            const typeInfo = getMessageTypeInfo(message.message_type);
            const TypeIcon = typeInfo.icon;
            
            return (
              <Card key={message.id} data-testid={`message-card-${message.id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Type Icon */}
                    <div className={`p-2 rounded-lg bg-muted h-fit ${typeInfo.color}`}>
                      <TypeIcon size={20} />
                    </div>

                    {/* Message Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-medium">{message.title}</h3>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(parseISO(message.created_at), 'MMM d, HH:mm')}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {message.message}
                      </p>

                      <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                        <User size={14} />
                        <span>{message.sent_by_name}</span>
                        <span className="px-1.5 py-0.5 bg-muted rounded text-xs">
                          {message.sent_by_role === 'team_manager' ? 'Manager' : 'Coach'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Team Message</DialogTitle>
            <DialogDescription>
              Send a message to all team coaches and managers. They will receive a notification.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Training Schedule Update"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="message-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Message Type</Label>
              <Select 
                value={formData.message_type} 
                onValueChange={(v) => setFormData({ ...formData, message_type: v })}
              >
                <SelectTrigger data-testid="message-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {messageTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon size={16} className={type.color} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Write your message here..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={5}
                required
                data-testid="message-content-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="send-message-btn">
                {saving ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamMessages;
